import React, { useState, useEffect } from 'react';
import { FileText, Plus, Edit, Trash2, Copy, X, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    content: '',
    placeholders: []
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/templates/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to load templates:', error);
      
      // If no templates exist, seed defaults
      if (error.response?.status === 404 || templates.length === 0) {
        await seedDefaultTemplates();
      }
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/templates/seed-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadTemplates();
      toast.success('Default templates loaded');
    } catch (error) {
      console.error('Failed to seed templates:', error);
    }
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      category: 'Legal',
      description: '',
      content: '',
      placeholders: []
    });
    setShowCreateModal(true);
  };

  const handleEdit = (template) => {
    if (template.is_default) {
      toast.error('Cannot edit default templates. Clone it to create a custom version.');
      return;
    }
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      category: template.category,
      description: template.description,
      content: template.content,
      placeholders: template.placeholders || []
    });
    setShowEditModal(true);
  };

  const handleClone = async (template) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/templates/${template.id}/clone`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template cloned successfully!');
      await loadTemplates();
    } catch (error) {
      toast.error('Failed to clone template');
    }
  };

  const handleDelete = async (template) => {
    if (template.is_default) {
      toast.error('Cannot delete default templates');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/templates/${template.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted successfully!');
      await loadTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      if (showEditModal && selectedTemplate) {
        await axios.put(`${API_URL}/api/templates/${selectedTemplate.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/templates/`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template created successfully!');
      }
      
      setShowCreateModal(false);
      setShowEditModal(false);
      await loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const detectPlaceholders = (content) => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches = [...content.matchAll(regex)];
    return [...new Set(matches.map(m => m[1]))];
  };

  const handleContentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      content: value,
      placeholders: detectPlaceholders(value)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Template Management</h2>
          <p className="text-slate-600">Create and manage document templates</p>
        </div>
        <button
          onClick={handleCreate}
          className="primary-button flex items-center gap-2"
        >
          <Plus size={20} />
          Create New Template
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div
            key={template.id}
            className="border border-slate-200 rounded-lg p-6 hover:border-purple-300 hover:shadow-lg transition-all"
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <FileText className="text-purple-600" size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-slate-900 truncate">{template.name}</h3>
                  {template.is_default && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Default</span>
                  )}
                </div>
                <span className="inline-block px-2 py-1 bg-cyan-100 text-cyan-700 text-xs rounded mb-2">
                  {template.category}
                </span>
                <p className="text-sm text-slate-600">{template.description}</p>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={() => handleEdit(template)}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-2 text-sm"
                disabled={template.is_default}
              >
                <Edit size={16} />
                Edit
              </button>
              <button
                onClick={() => handleClone(template)}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-2 text-sm"
              >
                <Copy size={16} />
                Clone
              </button>
              <button
                onClick={() => handleDelete(template)}
                className="px-3 py-2 border border-red-300 rounded-lg text-red-700 hover:bg-red-50 flex items-center justify-center gap-2 text-sm"
                disabled={template.is_default}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-slate-900">
                {showEditModal ? 'Edit Template' : 'Create New Template'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Template Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    >
                      <option value="Legal">Legal</option>
                      <option value="Engagement">Engagement</option>
                      <option value="Communication">Communication</option>
                      <option value="Financial">Financial</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Description *
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Template Content (HTML) *
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => handleContentChange(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none font-mono text-sm"
                    rows={15}
                    placeholder="Enter HTML content with placeholders like {{client_name}}, {{frozen_amount}}, etc."
                    required
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Use double curly braces for placeholders: {'{{'} placeholder_name {'}}'}
                  </p>
                </div>

                {formData.placeholders.length > 0 && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-slate-900 mb-2">Detected Placeholders:</h4>
                    <div className="flex flex-wrap gap-2">
                      {formData.placeholders.map((placeholder, idx) => (
                        <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                          {'{{'}{placeholder}{'}}'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-4 mt-6 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                  }}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 primary-button py-3 flex items-center justify-center gap-2"
                >
                  <Save size={20} />
                  {showEditModal ? 'Update Template' : 'Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManager;
